/**
 * slug.ts — the single kebab-case slug derivation for the whole monorepo.
 *
 * Repo convention: `name` is the human-readable label, `slug` is the kebab-case system
 * identifier used in URLs, lookups and unique indexes.
 *
 * ## Why this file exists
 *
 * Before it, fourteen `slugify` functions and ~28 inline `toLowerCase().replace(...)` chains
 * derived slugs independently, and they did not agree. Three divergences were load-bearing:
 *
 * 1. **Truncation could leave a dangling separator.** Most copies trimmed leading/trailing
 *    `-` and *then* truncated, so a cut landing on a separator emitted `my-strategy-`. The
 *    client copy re-trimmed after truncating and emitted `my-strategy`. Same name, two slugs,
 *    and the mismatch only showed up on long names.
 *
 * 2. **The empty slug was reachable.** A name with no ASCII alphanumerics at all ("Ημέρα",
 *    "日本語", "!!!") collapsed to `''` in the copies that had no fallback — the client one
 *    among them, which is the copy that POSTs the value. `''` is not a valid identifier
 *    anywhere it lands.
 *
 * 3. **Accented letters were silently dropped by most copies.** Only `makeConversationSlug`
 *    ran `.normalize('NFKD')` first; everywhere else `Café` slugged to `caf`, which both loses
 *    a letter and collides with the genuinely different name `Caf`. Folding the accent is the
 *    correct reading and is now the default — safe to change because nothing in the repo
 *    re-derives a slug in order to look up an existing row (every `findBySlug` takes the stored
 *    or URL value), so this only affects how new slugs are minted.
 *
 * 4. **Per-site truncation limits ranged from 60 to 200 characters.** A name slugged by the
 *    client at 60 and by a server service at 70 produced two different rows for one entity.
 *    The limit is still a per-caller choice (schemas genuinely differ), but it is now an
 *    explicit argument rather than a constant buried in a private copy.
 *
 * ## The camelCase splitter
 *
 * Two copies carried `.replace(/([a-z0-9])([A-Z])/g, '$1-$2')` intending `TrendFollowing` →
 * `trend-following`. It never ran: both applied `.toLowerCase()` on the line above, so `[A-Z]`
 * could not match. Every slug ever persisted therefore reads `trendfollowing`.
 *
 * The intent was real and unfinished, so it is completed here as `splitCamelCase` — but it is
 * **off by default**, because switching it on globally would re-derive a different slug for
 * every existing row and break lookups against data already written. A caller that wants the
 * split must ask for it, and only for identifiers it is minting fresh.
 */

/**
 * Longest slug the strictest current API schema accepts. Callers whose column or schema is
 * narrower pass their own `maxLength`.
 */
export const DEFAULT_SLUG_MAX_LENGTH = 200;

export interface SlugifyOptions {
  /**
   * Returned when the input contains nothing sluggable. Defaults to `'item'` so a slug that is
   * about to be stored is never `''`.
   *
   * Pass `''` deliberately where the caller's next line asks "was there anything sluggable at
   * all?" and treats the empty result as "no" — a few canonical-key builders do exactly that,
   * and giving them a fallback would turn a rejection into a fabricated key.
   */
  fallback?: string;
  /** Truncation limit, applied so the result never ends on a separator. */
  maxLength?: number;
  /**
   * Insert a separator at camelCase boundaries before lowercasing (`TrendFollowing` →
   * `trend-following`). Off by default: see the file header — turning it on changes the slug
   * derived for names that already have rows.
   */
  splitCamelCase?: boolean;
  /**
   * Character that replaces each run of non-alphanumerics. `-` for slugs; pass `_` where the
   * result has to be a snake_case identifier rather than a URL segment.
   */
  separator?: '-' | '_';
  /**
   * Decompose accented characters and drop the combining marks, so `Café` → `cafe` rather than
   * `caf`. On by default; pass `false` only to reproduce a legacy slug verbatim.
   *
   * This folds a base letter plus a combining mark. It is not transliteration: standalone
   * letters with no decomposition (`Æ`, `ß`, any CJK character) are still dropped as
   * non-alphanumerics.
   */
  stripDiacritics?: boolean;
}

/**
 * Derive a kebab-case slug from a human-readable name.
 *
 * Collapses every run of non-alphanumerics into a single `-`, trims separators from both ends,
 * truncates to `maxLength` without leaving a trailing `-`, and returns `fallback` if nothing
 * sluggable remains.
 */
export function slugify(value: string, options: SlugifyOptions = {}): string {
  const {
    fallback = 'item',
    maxLength = DEFAULT_SLUG_MAX_LENGTH,
    splitCamelCase = false,
    separator = '-',
    stripDiacritics = true,
  } = options;

  const cased = splitCamelCase ? value.replace(/([a-z0-9])([A-Z])/g, `$1${separator}$2`) : value;
  // NFKD splits `é` into `e` + U+0301; the range below removes the combining marks it produced.
  const source = stripDiacritics ? cased.normalize('NFKD').replace(/[\u0300-\u036f]/g, '') : cased;
  const slug = source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, separator)
    .replace(new RegExp(`^\\${separator}+|\\${separator}+$`, 'g'), '')
    .slice(0, maxLength)
    // Truncation can land mid-separator; re-trim so the result never ends on one.
    .replace(new RegExp(`\\${separator}+$`), '');

  return slug || fallback;
}

/**
 * True when `value` is already exactly what `slugify` would produce for it — i.e. a
 * well-formed slug. Use for validating slugs supplied by a caller rather than derived.
 */
export function isSlug(value: string, options: SlugifyOptions = {}): boolean {
  return value.length > 0 && slugify(value, options) === value;
}

/**
 * A comparison key: lower-cased with every non-alphanumeric removed outright (no separator).
 *
 * This is deliberately NOT a slug. Slugs are identifiers that get stored and shown; this is a
 * throwaway key for asking "are these two strings the same thing modulo punctuation" —
 * `openai/gpt-4o` and `OpenAI GPT 4o` both reduce to `openaigpt4o`. Keeping it here, beside
 * `slugify`, is what stops it being re-derived as a fourth private one-liner.
 */
export function alphanumericKey(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}
